import api from './api';

// ========================================
// TYPES
// ========================================

export type VisitMode = 'in_person' | 'video' | 'async_message';

export interface EmrAppointment {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string;
  service_type_id?: string;
  location_id?: string;
  start_time: string;
  end_time: string;
  duration: number;
  status: 'scheduled' | 'confirmed' | 'checked_in' | 'in_progress' | 'completed' | 'no_show' | 'cancelled' | 'rescheduled';
  title?: string;
  notes?: string;
  patient_notes?: string;
  visit_mode?: VisitMode;
  is_telehealth?: boolean;
  telehealth_link?: string;
  chat_conversation_id?: string;
  chat_conversation_url?: string;
  color?: string;
  created_at?: string;
  updated_at?: string;
  // Computed fields from backend
  patient_name?: string;
  practitioner_name?: string;
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
    dob?: string;
    phone?: string;
  };
  practitioner?: {
    id: string;
    first_name: string;
    last_name: string;
    credentials?: string;
  };
  serviceType?: {
    id: string;
    name: string;
    duration: number;
    color: string;
  };
  location?: {
    id: string;
    name: string;
    is_telehealth?: boolean;
  };
  // Linked invoice from the unified billing flow. Present when the
  // appointment was booked under a priced AppointmentType. Drives
  // the per-row payment-status badge in the clinic Appointments tab.
  invoice?: {
    id: string;
    invoice_number: string | null;
    status: 'draft' | 'open' | 'sent' | 'partially_paid' | 'paid' | 'void';
    total_cents: number;
    balance_cents: number;
    amount_paid_cents: number;
    payment_page_token: string | null;
  } | null;
}

export interface EmrServiceType {
  id: string;
  name: string;
  description?: string;
  duration: number;
  color: string;
  is_active: boolean;
  sort_order?: number;
}

export interface EmrLocation {
  id: string;
  clinic_id: string;
  name: string;
  address_line_1?: string;
  address_line_2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  phone?: string;
  is_active: boolean;
  is_default?: boolean;
}

export interface EmrProviderSchedule {
  id: string;
  clinic_id: string;
  practitioner_id: string;
  day_of_week: number;
  start_time: string;
  end_time: string;
  location_id?: string;
  is_active: boolean;
}

export interface EmrEncounter {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string;
  appointment_id?: string;
  start_time: string;
  end_time?: string;
  encounter_type: string;
  status: 'scheduled' | 'in_progress' | 'completed' | 'cancelled' | 'no_show';
  chief_complaint?: string;
  vitals?: {
    blood_pressure_systolic?: number;
    blood_pressure_diastolic?: number;
    heart_rate?: number;
    temperature?: number;
    weight?: number;
    height?: number;
    oxygen_saturation?: number;
    respiratory_rate?: number;
  };
  diagnoses?: string[];
  procedures?: string[];
  billing_codes?: string[];
  follow_up_date?: string;
  follow_up_notes?: string;
  signed_at?: string;
  signed_by?: string;
  patient?: {
    id: string;
    first_name: string;
    last_name: string;
  };
  practitioner?: {
    id: string;
    first_name: string;
    last_name: string;
    credentials?: string;
  };
}

export interface EmrClinicalNote {
  id: string;
  clinic_id: string;
  patient_id: string;
  practitioner_id: string;
  encounter_id?: string;
  appointment_id?: string;
  note_type: 'soap' | 'progress' | 'chart_note' | 'treatment_plan' | 'assessment' | 'procedure_note' | 'other';
  note_date: string;
  subjective?: string;
  objective?: string;
  assessment?: string;
  plan?: string;
  content?: string;
  is_signed: boolean;
  signed_at?: string;
  signed_by?: string;
  is_addendum: boolean;
  is_locked: boolean;
  parent_note_id?: string;
  created_at?: string;
  updated_at?: string;
  practitioner?: {
    id: string;
    first_name: string;
    last_name: string;
    credentials?: string;
  };
  addendums?: EmrClinicalNote[];
}

export interface CreateAppointmentLabOrder {
  panel_id?: string;
  panel_name?: string;
  test_ids?: string[];
  collection_method: 'walk_in' | 'at_home_phlebotomy';
  lab_center_id?: string;
  deadline_days_before?: number;
  notes?: string;
}

export interface CreateAppointmentDto {
  patient_id: string;
  practitioner_id: string;
  service_type_id?: string;
  appointment_type_id?: string;
  location_id?: string;
  start_time: string;
  duration: number;
  title?: string;
  notes?: string;
  patient_notes?: string;
  is_telehealth?: boolean;
  visit_mode?: string;
  status?: string;
  // Pre-visit intake (server fires intakeService.createPendingIntake when set)
  send_intake?: boolean;
  intake_form_template_id?: string;
  therapy_category?: string;
  // Pre-visit lab orders. Server creates one LabOrder per entry, each
  // linked to the new appointment via internal_appointment_id and
  // off_encounter_reason='pre_visit_lab'. Failures are non-fatal and
  // surface as `lab_orders: [{success, orderId?, error?}]` in the response.
  lab_orders?: CreateAppointmentLabOrder[];
}

export interface UpdateAppointmentDto extends Partial<CreateAppointmentDto> {
  id: string;
}

export interface AppointmentFilters {
  providerId?: string;
  locationId?: string;
  status?: string;
  startDate?: string;
  endDate?: string;
  patientId?: string;
}

// ========================================
// API SERVICE
// ========================================

export const emrService = {
  // ========================================
  // APPOINTMENTS
  // ========================================

  async getAppointments(
    clinicId: string,
    filters?: AppointmentFilters
  ): Promise<EmrAppointment[]> {
    const params = new URLSearchParams();
    if (filters?.providerId) params.append('practitionerId', filters.providerId);
    if (filters?.locationId) params.append('location_id', filters.locationId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);
    if (filters?.patientId) params.append('patientId', filters.patientId);

    const response = await api.get(
      `/emr/${clinicId}/appointments?${params.toString()}`
    );
    return response.data?.appointments || response.data;
  },

  async getPatientAppointments(
    clinicId: string,
    patientId: string
  ): Promise<EmrAppointment[]> {
    const response = await api.get(
      `/emr/${clinicId}/appointments?patientId=${patientId}`
    );
    return response.data?.appointments || response.data || [];
  },

  async getAppointment(clinicId: string, appointmentId: string): Promise<EmrAppointment> {
    const response = await api.get(`/emr/${clinicId}/appointments/${appointmentId}`);
    return response.data;
  },

  async createAppointment(
    clinicId: string,
    data: CreateAppointmentDto
  ): Promise<EmrAppointment> {
    const response = await api.post(`/emr/${clinicId}/appointments`, data);
    return response.data;
  },

  async updateAppointment(
    clinicId: string,
    appointmentId: string,
    data: Partial<CreateAppointmentDto>
  ): Promise<EmrAppointment> {
    const response = await api.put(`/emr/${clinicId}/appointments/${appointmentId}`, data);
    return response.data;
  },

  async resendAppointmentLink(
    clinicId: string,
    appointmentId: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await api.post(`/emr/${clinicId}/appointments/${appointmentId}/resend-link`);
    return response.data;
  },

  async deleteAppointment(clinicId: string, appointmentId: string): Promise<void> {
    try {
      const response = await api.delete(`/emr/${clinicId}/appointments/${appointmentId}`);
      return response;
    } catch (error: any) {
      console.error('Error in deleteAppointment service:', error);
      throw error;
    }
  },

  async updateAppointmentStatus(
    clinicId: string,
    appointmentId: string,
    status: string
  ): Promise<EmrAppointment> {
    const response = await api.patch(`/emr/${clinicId}/appointments/${appointmentId}/status`, {
      status,
    });
    return response.data;
  },

  // ========================================
  // SERVICE TYPES
  // ========================================

  async getServiceTypes(): Promise<EmrServiceType[]> {
    const response = await api.get('/emr/service-types');
    return response.data?.serviceTypes || response.data || [];
  },

  async createServiceType(
    data: Omit<EmrServiceType, 'id'>
  ): Promise<EmrServiceType> {
    const response = await api.post('/emr/service-types', data);
    return response.data;
  },

  async updateServiceType(
    id: string,
    data: Partial<EmrServiceType>
  ): Promise<EmrServiceType> {
    const response = await api.put(`/emr/service-types/${id}`, data);
    return response.data;
  },

  async deleteServiceType(id: string): Promise<void> {
    await api.delete(`/emr/service-types/${id}`);
  },

  // ========================================
  // LOCATIONS
  // ========================================

  async getLocations(clinicId: string): Promise<EmrLocation[]> {
    const response = await api.get(`/emr/${clinicId}/locations`);
    return response.data?.locations || response.data || [];
  },

  async createLocation(
    clinicId: string,
    data: Omit<EmrLocation, 'id' | 'clinic_id'>
  ): Promise<EmrLocation> {
    const response = await api.post(`/emr/${clinicId}/locations`, data);
    return response.data;
  },

  async updateLocation(
    clinicId: string,
    id: string,
    data: Partial<EmrLocation>
  ): Promise<EmrLocation> {
    const response = await api.put(`/emr/${clinicId}/locations/${id}`, data);
    return response.data;
  },

  async deleteLocation(clinicId: string, id: string): Promise<void> {
    await api.delete(`/emr/${clinicId}/locations/${id}`);
  },

  // ========================================
  // PROVIDER SCHEDULES
  // ========================================

  async getProviderSchedules(
    clinicId: string,
    providerId?: string
  ): Promise<EmrProviderSchedule[]> {
    const params = providerId ? `?practitioner_id=${providerId}` : '';
    const response = await api.get(`/emr/${clinicId}/schedules${params}`);
    return response.data?.schedules || response.data || [];
  },

  async createProviderSchedule(
    clinicId: string,
    data: Omit<EmrProviderSchedule, 'id' | 'clinic_id'>
  ): Promise<EmrProviderSchedule> {
    const response = await api.post(`/emr/${clinicId}/schedules`, data);
    return response.data;
  },

  async updateProviderSchedule(
    clinicId: string,
    id: string,
    data: Partial<EmrProviderSchedule>
  ): Promise<EmrProviderSchedule> {
    const response = await api.put(`/emr/${clinicId}/schedules/${id}`, data);
    return response.data;
  },

  async deleteProviderSchedule(clinicId: string, id: string): Promise<void> {
    await api.delete(`/emr/${clinicId}/schedules/${id}`);
  },

  // ========================================
  // ENCOUNTERS
  // ========================================

  async getEncounters(
    clinicId: string,
    filters?: { patientId?: string; providerId?: string; startDate?: string; endDate?: string }
  ): Promise<EmrEncounter[]> {
    const params = new URLSearchParams();
    if (filters?.patientId) params.append('patientId', filters.patientId);
    if (filters?.providerId) params.append('practitioner_id', filters.providerId);
    if (filters?.startDate) params.append('start_date', filters.startDate);
    if (filters?.endDate) params.append('end_date', filters.endDate);

    const response = await api.get(
      `/emr/${clinicId}/encounters?${params.toString()}`
    );
    return response.data?.encounters || response.data || [];
  },

  async getEncounter(clinicId: string, encounterId: string): Promise<EmrEncounter> {
    const response = await api.get(`/emr/${clinicId}/encounters/${encounterId}`);
    return response.data;
  },

  async createEncounter(
    clinicId: string,
    data: Omit<EmrEncounter, 'id' | 'clinic_id' | 'created_at' | 'updated_at'>
  ): Promise<EmrEncounter> {
    const response = await api.post(`/emr/${clinicId}/encounters`, data);
    return response.data;
  },

  async updateEncounter(
    clinicId: string,
    encounterId: string,
    data: Partial<EmrEncounter>
  ): Promise<EmrEncounter> {
    const response = await api.put(`/emr/${clinicId}/encounters/${encounterId}`, data);
    return response.data;
  },

  async signEncounter(clinicId: string, encounterId: string): Promise<EmrEncounter> {
    const response = await api.post(`/emr/${clinicId}/encounters/${encounterId}/sign`);
    return response.data;
  },

  // ========================================
  // CLINICAL NOTES
  // ========================================

  async getNotes(
    clinicId: string,
    filters?: { patientId?: string; encounterId?: string; noteType?: string }
  ): Promise<EmrClinicalNote[]> {
    const params = new URLSearchParams();
    if (filters?.patientId) params.append('patientId', filters.patientId);
    if (filters?.encounterId) params.append('encounter_id', filters.encounterId);
    if (filters?.noteType) params.append('note_type', filters.noteType);

    const response = await api.get(
      `/emr/${clinicId}/notes?${params.toString()}`
    );
    return response.data?.notes || response.data || [];
  },

  async getNote(clinicId: string, noteId: string): Promise<EmrClinicalNote> {
    const response = await api.get(`/emr/${clinicId}/notes/${noteId}`);
    return response.data;
  },

  async createNote(
    clinicId: string,
    data: Omit<EmrClinicalNote, 'id' | 'clinic_id' | 'created_at' | 'updated_at'>
  ): Promise<EmrClinicalNote> {
    const response = await api.post(`/emr/${clinicId}/notes`, data);
    return response.data;
  },

  async updateNote(
    clinicId: string,
    noteId: string,
    data: Partial<EmrClinicalNote>
  ): Promise<EmrClinicalNote> {
    const response = await api.put(`/emr/${clinicId}/notes/${noteId}`, data);
    return response.data;
  },

  async signNote(clinicId: string, noteId: string): Promise<EmrClinicalNote> {
    const response = await api.post(`/emr/${clinicId}/notes/${noteId}/sign`);
    return response.data;
  },

  async createAddendum(
    clinicId: string,
    noteId: string,
    content: string
  ): Promise<EmrClinicalNote> {
    const response = await api.post(`/emr/${clinicId}/notes/${noteId}/addendum`, {
      content,
    });
    return response.data;
  },

  // ========================================
  // PATIENT NOTES (BY PATIENT)
  // ========================================

  async getPatientNotes(clinicId: string, patientId: string): Promise<EmrClinicalNote[]> {
    const response = await api.get(`/emr/${clinicId}/notes?patientId=${patientId}`);
    return response.data?.notes || response.data || [];
  },

  async getPatientNotesPaginated(
    clinicId: string,
    patientId: string,
    options?: { page?: number; limit?: number; noteType?: EmrClinicalNote['note_type'] }
  ): Promise<{
    data: EmrClinicalNote[];
    pagination: { total: number; page: number; limit: number; totalPages: number };
  }> {
    const params = new URLSearchParams();
    params.append('patientId', patientId);
    if (options?.page) params.append('page', String(options.page));
    if (options?.limit) params.append('limit', String(options.limit));
    if (options?.noteType) params.append('noteType', options.noteType);
    const response = await api.get(`/emr/${clinicId}/notes?${params.toString()}`);
    return {
      data: response.data?.notes || [],
      pagination: response.data?.pagination || { total: 0, page: 1, limit: 10, totalPages: 1 },
    };
  },

  async getPatientEncounters(clinicId: string, patientId: string): Promise<EmrEncounter[]> {
    const response = await api.get(`/emr/${clinicId}/encounters?patientId=${patientId}`);
    return response.data?.encounters || response.data || [];
  },

  /**
   * Returns the patient's currently in-progress (un-signed) encounter,
   * or null if none. Used by the standalone lab-order flow to suggest
   * linking a new order to the active visit instead of orphaning it.
   */
  async getActiveEncounterForPatient(
    clinicId: string,
    patientId: string,
  ): Promise<EmrEncounter | null> {
    const response = await api.get(
      `/emr/${clinicId}/patients/${patientId}/encounters/active`,
    );
    return response.data?.encounter || null;
  },

  // ========================================
  // PROVIDER AVAILABILITY (Internal)
  // ========================================

  async getProviderAvailableSlots(
    clinicId: string,
    practitionerId: string,
    date: string,
    timezone?: string,
    appointmentTypeId?: string
  ): Promise<{
    slots: Array<{ start: string; end: string; local_start?: string; local_end?: string }>;
    timezone: string;
    duration: number;
    date: string;
  }> {
    const params = new URLSearchParams({ date });
    if (timezone) params.append('timezone', timezone);
    if (appointmentTypeId) params.append('appointment_type_id', appointmentTypeId);
    const response = await api.get(
      `/emr/${clinicId}/providers/${practitionerId}/available-slots?${params.toString()}`
    );
    return response.data?.data || response.data || { slots: [], timezone: '', duration: 30, date };
  },

  async getProviderAvailableDates(
    clinicId: string,
    practitionerId: string,
    year: number,
    month: number,
    timezone?: string
  ): Promise<{
    year: number;
    month: number;
    dates: Array<{ date: string; hasSlots: boolean }>;
    timezone: string;
  }> {
    const params = new URLSearchParams({
      year: year.toString(),
      month: month.toString(),
    });
    if (timezone) params.append('timezone', timezone);
    const response = await api.get(
      `/emr/${clinicId}/providers/${practitionerId}/available-dates?${params.toString()}`
    );
    return response.data?.data || response.data || { year, month, dates: [], timezone: '' };
  },

  // ── Phase 3: ambient capture + pre-visit summary (stubs on the BE) ──

  async startEncounterTranscript(
    clinicId: string, encounterId: string,
  ): Promise<{ transcriptId: string; startedAt: string; stub?: boolean; message?: string }> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/transcripts/start`,
    );
    return response.data?.data || response.data;
  },

  async stopEncounterTranscript(
    clinicId: string, encounterId: string,
    payload: { transcriptId?: string; durationSeconds?: number },
  ): Promise<{ status: string; soapGenerationStatus?: string; stub?: boolean; message?: string }> {
    const response = await api.post(
      `/emr/${clinicId}/encounters/${encounterId}/transcripts/stop`,
      payload,
    );
    return response.data?.data || response.data;
  },

  async getEncounterPreVisitSummary(
    clinicId: string, encounterId: string,
  ): Promise<{
    bullets: string[];
    severity: 'normal' | 'attention' | 'critical';
    generatedAt: string;
    stub?: boolean;
    message?: string;
  }> {
    const response = await api.get(
      `/emr/${clinicId}/encounters/${encounterId}/pre-visit-summary`,
    );
    return response.data?.data || response.data;
  },
};

export default emrService;
